/**
 * The border-box size layout actually USED for an element, fractional.
 *
 * A card enlarged in place holds its resting footprint in the row: a zoom plus
 * margins that compensate it, computed from the size measured at rest
 * (cards.js applyHoverTransform, crisp-hover.js). Whatever that measurement is
 * off by comes back multiplied by the zoom. `offsetWidth`/`offsetHeight` round
 * to whole pixels — a 170.5 px card reads as 170, and at the hand's 3.8x hover
 * that half pixel became 1.4 px of hand row, which is the height the battle
 * column shrinks the row to: the player's own board moved under its hand
 * (measured 2026-09-28, 1024x768).
 *
 * getComputedStyle answers the used `width`/`height`, fractional, in the
 * element's own box-sizing; a rotated card has no bounding box that could
 * stand in for it. Where layout reports nothing (jsdom, a detached node) the
 * offset size is the fallback.
 *
 * @param {HTMLElement} element
 * @param {CSSStyleDeclaration} [style] the element's computed style, when the caller already has it
 * @returns {{ width: number, height: number }}
 */
export function usedBoxSize(element, style = getComputedStyle(element)) {
    return {
        width: usedLength(style, 'width') || Number(element.offsetWidth) || 0,
        height: usedLength(style, 'height') || Number(element.offsetHeight) || 0,
    };
}

/**
 * @param {CSSStyleDeclaration} style
 * @param {'width' | 'height'} axis
 */
function usedLength(style, axis) {
    const value = parseFloat(style.getPropertyValue(axis));
    if (!(value > 0)) return 0;
    if (style.getPropertyValue('box-sizing') === 'border-box') return value;
    const [a, b] = axis === 'width' ? ['left', 'right'] : ['top', 'bottom'];
    /** @param {string} name */
    const px = (name) => parseFloat(style.getPropertyValue(name)) || 0;
    return value
        + px(`padding-${a}`) + px(`padding-${b}`)
        + px(`border-${a}-width`) + px(`border-${b}-width`);
}
